import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Typography,
    Stack,
    Box,
    Button,
    CircularProgress,
    Chip,
    Divider,
    Alert,
} from '@mui/material';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const fmt = (amount, currency = 'GBP') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(amount) || 0);

const STATUS_COLORS = { draft: 'default', sent: 'info', paid: 'success', overdue: 'warning', void: 'default' };

const recipientLabel = (invoice) => {
    if (invoice.parentId) {
        return {
            name: `${invoice.parentId.firstName || ''} ${invoice.parentId.surname || ''}`.trim(),
            email: invoice.parentId.email,
        };
    }
    return {
        name: invoice.recipientName || '',
        email: invoice.recipientEmail || '',
    };
};

export default function InvoiceDetailPage() {
    const confirm = useConfirm();
    const { id } = useParams();
    const [invoice, setInvoice] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const load = async () => {
        const { data } = await axios.get(`${API_URL}/api/admin/invoices/${id}`, authHeaders());
        setInvoice(data);
    };

    useEffect(() => {
        load();
    }, [id]);

    const sendNow = async () => {
        setBusy(true);
        setMessage('');
        setError('');
        try {
            await axios.post(`${API_URL}/api/admin/invoices/${id}/send`, {}, authHeaders());
            await load();
            setMessage('Invoice sent.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const markPaid = async () => {
        setBusy(true);
        setMessage('');
        setError('');
        try {
            await axios.post(`${API_URL}/api/admin/invoices/${id}/mark-paid`, {}, authHeaders());
            await load();
            setMessage('Marked paid.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const previewPdf = async () => {
        try {
            const { data } = await axios.get(
                `${API_URL}/api/admin/invoices/${id}/preview-pdf`,
                { ...authHeaders(), responseType: 'blob' }
            );
            const blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
            window.open(blobUrl, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load preview.');
        }
    };

    const sendReminder = async () => {
        if (!(await confirm({ title: 'Send reminder', message: 'Send a payment reminder email to this recipient?', confirmLabel: 'Send' }))) return;
        setBusy(true);
        setMessage('');
        setError('');
        try {
            await axios.post(`${API_URL}/api/admin/invoices/${id}/send-reminder`, {}, authHeaders());
            await load();
            setMessage('Reminder sent.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    const markUnpaid = async () => {
        setBusy(true);
        setMessage('');
        setError('');
        try {
            await axios.post(`${API_URL}/api/admin/invoices/${id}/mark-unpaid`, {}, authHeaders());
            await load();
            setMessage('Marked unpaid.');
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    if (!invoice) {
        return (
            <Container>
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            </Container>
        );
    }

    const r = recipientLabel(invoice);
    const fromEnquiry = invoice.contactEnquiryId;

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="h4">{invoice.invoiceNumber}</Typography>
                    {invoice.paymentReference && (
                        <Chip label={`Ref: ${invoice.paymentReference}`} color="primary" />
                    )}
                    <Chip label={invoice.status} color={STATUS_COLORS[invoice.status] || 'default'} />
                    {invoice.paymentMarkedByParent && invoice.status !== 'paid' && (
                        <Chip label="Marked paid by parent" color="warning" />
                    )}
                </Stack>
                <Button component={RouterLink} to="/invoices">Back</Button>
            </Stack>

            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {invoice.paymentMarkedByParent && invoice.status !== 'paid' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The parent reported paying this invoice on {invoice.paymentMarkedAt ? new Date(invoice.paymentMarkedAt).toLocaleString('en-GB') : '—'}.
                    Verify the bank transfer, then click "Mark paid".
                </Alert>
            )}

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Bill to
                </Typography>
                <Typography>{r.name || '—'}</Typography>
                <Typography color="text.secondary">{r.email || '—'}</Typography>
                {fromEnquiry && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Created from contact enquiry —{' '}
                        <RouterLink to={`/contact-enquiries/${fromEnquiry._id || fromEnquiry}`}>
                            view enquiry
                        </RouterLink>
                    </Typography>
                )}
                {invoice.ccEmails?.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Also sent to: {invoice.ccEmails.join(', ')}
                    </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>Line items</Typography>
                {invoice.lineItems?.map((it, i) => (
                    <Stack direction="row" justifyContent="space-between" key={i} sx={{ py: 0.5 }}>
                        <Typography>{it.description} × {it.qty}</Typography>
                        <Typography>{fmt((it.qty || 1) * it.unitAmount, invoice.currency)}</Typography>
                    </Stack>
                ))}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6">{fmt(invoice.total, invoice.currency)}</Typography>
                </Stack>

                {invoice.bankDetailsSnapshot && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" sx={{ mb: 1 }}>Bank transfer details (snapshot)</Typography>
                        <Typography sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            {invoice.bankDetailsSnapshot}
                        </Typography>
                    </>
                )}

                {invoice.stripePaymentLinkUrl && (
                    <Box sx={{ mt: 2 }}>
                        <a href={invoice.stripePaymentLinkUrl} target="_blank" rel="noreferrer">
                            Stripe payment link
                        </a>
                    </Box>
                )}
                {invoice.pdfUrl && (
                    <Box sx={{ mt: 1 }}>
                        <a href={invoice.pdfUrl} target="_blank" rel="noreferrer">PDF</a>
                    </Box>
                )}
            </Card>

            {/* Action buttons — branded rotation:
                  primary   = the main destination action (Mark paid /
                              Send to recipient)
                  secondary = the important-but-not-final action
                              (Send reminder)
                  tertiary  = utility / undo (Preview, Duplicate, Edit,
                              Mark unpaid) */}
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap" rowGap={1}>
                <Button
                    variant="tertiary"
                    onClick={previewPdf}
                    disabled={busy}
                    title="Open the PDF the recipient will see — no email is sent"
                >
                    Preview PDF
                </Button>
                {invoice.status === 'draft' && (
                    <Button
                        component={RouterLink}
                        to={`/invoices/new?editId=${invoice._id}`}
                        variant="tertiary"
                        disabled={busy}
                    >
                        Edit
                    </Button>
                )}
                <Button
                    component={RouterLink}
                    to={`/invoices/new?duplicateOf=${invoice._id}`}
                    variant="tertiary"
                    disabled={busy}
                >
                    Duplicate
                </Button>
                {['sent', 'overdue'].includes(invoice.status) && (
                    <Button
                        variant="secondary"
                        onClick={sendReminder}
                        disabled={busy}
                    >
                        {busy ? 'Sending…' : 'Send reminder'}
                    </Button>
                )}
                {invoice.status === 'draft' && (
                    <Button variant="primary" onClick={sendNow} disabled={busy}>
                        {busy ? 'Sending…' : 'Send to recipient'}
                    </Button>
                )}
                {invoice.status !== 'paid' && (
                    <Button variant="primary" onClick={markPaid} disabled={busy}>
                        Mark paid
                    </Button>
                )}
                {invoice.status === 'paid' && (
                    <Button variant="tertiary" onClick={markUnpaid} disabled={busy}>
                        Mark unpaid
                    </Button>
                )}
            </Stack>
            {invoice.lastReminderSentAt && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
                    Last reminder sent {new Date(invoice.lastReminderSentAt).toLocaleString('en-GB')}
                    {invoice.remindersSentCount > 1 ? ` (${invoice.remindersSentCount}× total)` : ''}
                </Typography>
            )}
        </Container>
    );
}
